import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ToursSearch } from 'package-types';

import { valueAccessor } from '@helpers/angular.helper';
import { SearchFormStore } from '@services/store/search-form.store';

type DepartureCity = ToursSearch.DepartureCity & { country?: string };

@Component({
  selector: 'app-departure-city',
  templateUrl: './departure-city.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [valueAccessor(DepartureCityComponent)]
})
export class DepartureCityComponent implements ControlValueAccessor {
  departureCities$: Observable<DepartureCity[]> = this.localSearch.onChanges('departureCities')
    .pipe(
      map(cities => this.sortDepartureCities(cities))
    );
  departureCitiesLoading$ = this.localSearch.onChanges('departureCitiesStatus')
    .pipe(map(status => status === 'loading'));

  private static readonly priorityCountries = ['Молдова', 'Польща', 'Німеччина'];

  private selectedCityIdValue?: number;
  private onTouched: Function = () => { };
  private onChange: Function = () => { };

  constructor(
    private readonly localSearch: SearchFormStore,
    private readonly cd: ChangeDetectorRef
  ) { }

  get selectedCity() {
    return this.selectedCityIdValue;
  }

  set selectedCity(selectedCityId) {
    this.selectedCityIdValue = selectedCityId;
    this.onTouched();
    this.onChange(selectedCityId);
  }

  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  writeValue(city: number): void {
    this.selectedCityIdValue = city;
    this.cd.markForCheck();
  }

  searchDepartureCities(term: string, item: DepartureCity): boolean {
    const normalizedTerm = term.toLocaleLowerCase().trim();

    if (!normalizedTerm) {
      return true;
    }

    return (item.country ?? '').toLocaleLowerCase().includes(normalizedTerm)
      || item.name.toLocaleLowerCase().includes(normalizedTerm);
  }

  private sortDepartureCities(cities: DepartureCity[]): DepartureCity[] {
    const priorityCountryIndexes = new Map(
      DepartureCityComponent.priorityCountries.map((country, index) => [country, index])
    );

    return [...cities]
      .sort((left, right) => {
        const leftPriority = priorityCountryIndexes.get(left.country ?? '') ?? Number.MAX_SAFE_INTEGER;
        const rightPriority = priorityCountryIndexes.get(right.country ?? '') ?? Number.MAX_SAFE_INTEGER;

        return leftPriority - rightPriority
          || (left.country ?? '').localeCompare(right.country ?? '')
          || left.name.localeCompare(right.name);
      });
  }
}
